import React, { useState, useEffect, useMemo } from "react";
import { BsArrowLeft, BsPlayFill, BsBook } from "react-icons/bs";
import { FiChevronRight, FiChevronLeft, FiSearch, FiX } from "react-icons/fi";
import { HiMenuAlt2 } from "react-icons/hi";

const MachineLearning = () => {
  // --------------------------------------------------------------------------
  // DATA STRUCTURE
  // --------------------------------------------------------------------------
  const categoryStructure = useMemo(() => [
    {
      name: "Fundamentals",
      description: "Core concepts every ML engineer needs to know.",
      color: "from-blue-500 to-cyan-500",
      items: [
        {
          id: "1EddyrQewPLRkr5QJJWSP30NBRvuIe_cq",
          title: "Bias & Variance",
          category: "Fundamentals",
          duration: "15 min read"
        }
      ]
    },
    {
      name: "Regression",
      description: "Predicting continuous values using linear models.",
      color: "from-orange-500 to-red-500",
      items: [
        {
          id: "1EvkkkfxdsKGYcW-ytO2TIx6mh8qHk_8l",
          title: "Linear Regression",
          category: "Regression",
          duration: "20 min read"
        }
      ]
    },
    {
      name: "Tree Models",
      description: "Powerful non-linear models for classification and regression.",
      color: "from-green-500 to-emerald-500",
      items: [
        {
          id: "1PXeoWuLYlk7nSzuPEh4Ys0Ly2DrmbXmJ",
          title: "Decision Trees",
          category: "Tree Models",
          duration: "25 min read"
        },
        {
          id: "1q-pOFD1u6BOpZrwi01TmZNRn5mhUb87e",
          title: "Random Forests",
          category: "Tree Models",
          duration: "30 min read"
        }
      ]
    },
    {
      name: "Ensemble Methods",
      description: "Combining multiple models to improve performance.",
      color: "from-purple-500 to-violet-500",
      items: [
        {
          id: "1gOacTvNjsK5zUMiKGuj3FUVnu51qfu3X",
          title: "Ada Boost",
          category: "Ensemble Methods",
          duration: "25 min read"
        },
        {
          id: "1UpT0H7c_x5DideEx-zDDv6zU7vUtYvVO",
          title: "Gradient Boosting",
          category: "Ensemble Methods",
          duration: "35 min read"
        }
      ]
    },
    {
      name: "Experiments",
      description: "Designing and analyzing rigorous experiments.",
      color: "from-pink-500 to-rose-500",
      items: [
        {
          id: "1B5JLOJ28_vyOxQwad8qJHdwyBC3OajEJ",
          title: "How to Design an A/B Test",
          category: "Experiments",
          duration: "40 min read"
        }
      ]
    },
    {
      name: "Clustering",
      description: "Unsupervised learning to find patterns in data.",
      color: "from-teal-400 to-cyan-400",
      items: [
        {
          id: "13Ay2Tqf3xUmhqmdfoqOXk-HPoP4zsMoF",
          title: "K-Mean Clustering",
          category: "Clustering",
          duration: "20 min read"
        }
      ]
    },
    {
      name: "Classification",
      description: "Categorizing data points into classes.",
      color: "from-indigo-500 to-blue-600",
      items: [
        {
          id: "1GYDvJjRRbt6INHYDLxZAFjb0Cfby3HDd",
          title: "K-Nearest Neighbors",
          category: "Classification",
          duration: "15 min read"
        }
      ]
    }
  ], []);

  const mlGuides = useMemo(() => categoryStructure.flatMap(category => category.items), [categoryStructure]);

  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------
  const [viewMode, setViewMode] = useState("browse"); // 'browse' | 'read'
  const [selectedGuide, setSelectedGuide] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(true); // For reader mode
  const [loading, setLoading] = useState(false);
  const [pdfReady, setPdfReady] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);

  // --------------------------------------------------------------------------
  // EFFECTS & HANDLERS
  // --------------------------------------------------------------------------

  // Filter for Browse Mode
  const filteredCategories = useMemo(() => {
    if (!searchTerm) return categoryStructure;
    return categoryStructure.map(cat => ({
      ...cat,
      items: cat.items.filter(item => item.title.toLowerCase().includes(searchTerm.toLowerCase()))
    })).filter(cat => cat.items.length > 0);
  }, [categoryStructure, searchTerm]);

  const handleSelectGuide = (guide) => {
    setSelectedGuide(guide);
    setViewMode("read");
    setSidebarOpen(true); // Reset sidebar visibility when opening
  };

  const handleBackToLibrary = () => {
    setViewMode("browse");
    setSearchTerm("");
  };

  // PDF Loading Logic
  useEffect(() => {
    if (!selectedGuide || viewMode !== 'read') return;

    setLoading(true);
    setPdfReady(false);
    setIframeKey(prev => prev + 1);

    const timer = setTimeout(() => {
      setLoading(false);
      // Give a small buffer for the iframe to act alive
      setTimeout(() => setPdfReady(true), 500);
    }, 1500);

    return () => clearTimeout(timer);
  }, [selectedGuide, viewMode]);

  // Navigation Logic
  const handlePrev = () => {
    const idx = mlGuides.findIndex(g => g.id === selectedGuide.id);
    if (idx > 0) setSelectedGuide(mlGuides[idx - 1]);
  };

  const handleNext = () => {
    const idx = mlGuides.findIndex(g => g.id === selectedGuide.id);
    if (idx < mlGuides.length - 1) setSelectedGuide(mlGuides[idx + 1]);
  };

  // --------------------------------------------------------------------------
  // RENDER: BROWSE GRID
  // --------------------------------------------------------------------------
  const renderBrowse = () => (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0a1220] text-gray-900 dark:text-white p-6 md:p-12 transition-colors duration-300">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-amber-500 via-orange-500 to-red-500 dark:from-amber-300 dark:via-orange-500 dark:to-red-500 text-transparent bg-clip-text">
            Machine Learning Library
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg max-w-2xl mx-auto">
            Deep dive into the mathematics and code behind modern algorithms.
            Curated resources for data scientists.
          </p>

          {/* Search Bar */}
          <div className="mt-8 relative max-w-xl mx-auto group">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-xl blur-lg group-hover:blur-xl transition-all"></div>
            <div className="relative bg-white dark:bg-[#162032] border border-gray-200 dark:border-gray-700 rounded-xl flex items-center p-2 shadow-2xl transition-colors duration-300">
              <FiSearch className="text-gray-400 ml-3" size={20} />
              <input
                type="text"
                placeholder="Search for algorithm..."
                className="w-full bg-transparent border-none focus:ring-0 text-gray-900 dark:text-white placeholder-gray-500 px-4 py-2"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCategories.map((category) => (
            <div key={category.name} className="bg-white dark:bg-[#162032] rounded-2xl border border-gray-200 dark:border-gray-800 overflow-hidden hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-300 shadow-xl group hover:-translate-y-1">

              {/* Card Header */}
              <div className={`h-2 bg-gradient-to-r ${category.color}`}></div>
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">{category.name}</h3>
                  <span className="text-xs font-mono text-gray-500 bg-gray-100 dark:bg-[#0a1220] px-2 py-1 rounded">
                    {category.items.length} Modules
                  </span>
                </div>
                <p className="text-gray-500 dark:text-gray-400 text-sm mb-6 h-10 line-clamp-2">
                  {category.description}
                </p>

                {/* Modules List */}
                <div className="space-y-3">
                  {category.items.map(item => (
                    <button
                      key={item.id}
                      onClick={() => handleSelectGuide(item)}
                      className="w-full text-left flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-[#0a1220]/50 hover:bg-gray-100 dark:hover:bg-[#2d3b55] hover:pl-4 transition-all border border-gray-200 dark:border-gray-800/50 group-inner"
                    >
                      <div className="flex items-center gap-3">
                        <BsBook className="text-gray-400 dark:text-gray-500 group-inner-hover:text-blue-500 dark:group-inner-hover:text-blue-400 transition-colors" size={14} />
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-300 group-inner-hover:text-gray-900 dark:group-inner-hover:text-white transition-colors">
                          {item.title}
                        </span>
                      </div>
                      <FiChevronRight className="text-gray-400 dark:text-gray-600 opacity-0 group-inner-hover:opacity-100 transition-opacity" size={14} />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredCategories.length === 0 && (
          <div className="text-center py-20">
            <p className="text-gray-500 text-xl">No modules match your search.</p>
            <button
              onClick={() => setSearchTerm("")}
              className="mt-4 text-blue-400 hover:text-blue-300 underline"
            >
              Clear Search
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // --------------------------------------------------------------------------
  // RENDER: READER MODE
  // --------------------------------------------------------------------------
  if (viewMode === 'browse') return renderBrowse();

  return (
    <div className="h-screen w-full flex bg-gray-50 dark:bg-[#0a1220] text-gray-900 dark:text-white overflow-hidden transition-colors duration-300">

      {/* SIDEBAR (Course Content) */}
      <div
        className={`${sidebarOpen ? "w-80" : "w-0"} bg-white dark:bg-[#162032] border-r border-gray-200 dark:border-gray-800 transition-all duration-300 flex flex-col flex-shrink-0 z-20 absolute md:relative h-full shadow-xl md:shadow-none`}
      >
        <div className="p-5 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <button
            onClick={handleBackToLibrary}
            className="flex items-center text-xs font-bold text-gray-400 hover:text-[#f97316] uppercase tracking-wider transition-colors"
          >
            <BsArrowLeft className="mr-2" /> Library
          </button>

          <button onClick={() => setSidebarOpen(false)} className="md:hidden text-gray-400">
            <FiX size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-6">
          {categoryStructure.map(cat => (
            <div key={cat.name}>
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3 pl-2">
                {cat.name}
              </h4>
              <div className="space-y-1">
                {cat.items.map(item => {
                  const isActive = selectedGuide?.id === item.id;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleSelectGuide(item)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-3 ${isActive
                        ? "bg-blue-50 dark:bg-blue-600/10 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-600/20"
                        : "text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#0a1220] hover:text-gray-900 dark:hover:text-gray-200"
                        }`}
                    >
                      <div className={`w-2 h-2 rounded-full ${isActive ? "bg-blue-500 dark:bg-blue-400" : "bg-gray-400 dark:bg-gray-600"}`}></div>
                      <span className="truncate">{item.title}</span>
                      {isActive && <BsPlayFill className="ml-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MAIN CONTENT (PDF Viewer) */}
      <div className="flex-1 flex flex-col min-w-0 bg-gray-100 dark:bg-[#0a1220] relative transition-colors duration-300">

        {/* Top Header */}
        <header className="h-16 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between px-6 bg-white/90 dark:bg-[#0a1220]/90 backdrop-blur-md z-10 transition-colors duration-300">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 -ml-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <HiMenuAlt2 size={24} />
            </button>

            <div className="flex flex-col">
              <span className="text-xs text-gray-500 font-mono hidden md:block">{selectedGuide.category}</span>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white truncate max-w-[200px] md:max-w-md">
                {selectedGuide.title}
              </h2>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handlePrev}
              disabled={mlGuides.findIndex(g => g.id === selectedGuide.id) === 0}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 disabled:hover:text-gray-500 dark:disabled:hover:text-gray-400"
            >
              <FiChevronLeft size={24} />
            </button>
            <button
              onClick={handleNext}
              disabled={mlGuides.findIndex(g => g.id === selectedGuide.id) === mlGuides.length - 1}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 disabled:hover:text-gray-500 dark:disabled:hover:text-gray-400"
            >
              <FiChevronRight size={24} />
            </button>
          </div>
        </header>

        {/* Viewer Area */}
        <main className="flex-1 relative p-2 md:p-6 flex items-center justify-center">
          <div className="w-full max-w-5xl h-full bg-white dark:bg-[#162032] rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden relative">

            {loading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center z-10 bg-white dark:bg-[#162032]">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500 mb-4"></div>
                <p className="text-gray-500 dark:text-gray-400 animate-pulse">Loading content...</p>
              </div>
            )}

            <iframe
              key={iframeKey}
              src={`https://drive.google.com/file/d/${selectedGuide.id}/preview?page=1&usp=sharing&embedded=true`}
              title={selectedGuide.title}
              width="100%"
              height="100%"
              frameBorder="0"
              allowFullScreen
              className={`w-full h-full transition-opacity duration-700 ${pdfReady ? 'opacity-100' : 'opacity-0'}`}
            />
          </div>
        </main>
      </div>
    </div>
  );
};

export default MachineLearning;